/**
 * **Anlage einrichten** — vier Schritte bis zu den ersten Werten (HM-183,
 * UI-Durchsicht U5). Steht auf „Strom & Erzeugung“, solange die Anlage heute
 * nichts zeigt, und verschwindet danach von selbst.
 *
 * Die Schritte rechnet `lib/setup-steps.ts`; hier werden die Listen geladen, die
 * es dafür braucht — dieselben Abfrageschlüssel wie in den Bereichen, auf die die
 * Schritte zeigen, also kein zweiter Abruf, sobald man dort war.
 *
 * **Nur der nächste Schritt hat einen Knopf.** Vier Knöpfe nebeneinander ließen
 * offen, womit man anfängt. Wer die Anlage nur lesend geteilt bekommen hat,
 * sieht die Liste ohne Knöpfe — einrichten kann er nichts (Regel 10).
 */
import { Button, Card, icons } from "@hausmeter/ui";
import { useDevices, useMeters, useMeterSuggestions } from "../lib/queries.js";
import { navigate } from "../lib/router.js";
import { setupSteps } from "../lib/setup-steps.js";
import { useSiteAccess } from "../lib/site-access.js";

/** Für die Sprachausgabe: Haken und Ziffer allein sagen ihr nichts. */
const VISUALLY_HIDDEN = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
} as const;

export function SetupChecklist({
  siteId,
  concept,
  values,
  delivering,
}: {
  readonly siteId: string;
  readonly concept: boolean;
  readonly values: boolean;
  readonly delivering?: boolean;
}) {
  const devices = useDevices(siteId);
  const meters = useMeters(siteId);
  const suggestions = useMeterSuggestions(siteId);
  const rights = useSiteAccess();

  if (devices.isPending || meters.isPending || suggestions.isPending) {
    return <Card padding="sm">Wird geladen …</Card>;
  }

  const steps = setupSteps({
    devices: devices.data?.items.length ?? 0,
    meters: meters.data?.items.length ?? 0,
    openSuggestions: suggestions.data?.items.length ?? 0,
    concept,
    values,
    delivering,
  });

  return (
    <Card
      title="Anlage einrichten"
      subtitle={
        rights.canWrite
          ? "Vier Schritte, dann stehen hier Verbrauch und Erzeugung."
          : "Die Anlage wird gerade eingerichtet. Sobald sie fertig ist, stehen hier Verbrauch und Erzeugung."
      }
      padding="lg"
    >
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
        {steps.map((step, index) => (
          <li
            key={step.key}
            style={{
              display: "flex",
              gap: "var(--sp-4)",
              alignItems: "flex-start",
              padding: "var(--sp-4) 0",
              borderTop: index === 0 ? "none" : "1px solid var(--border-subtle)",
            }}
          >
            <span
              aria-hidden
              style={{
                flexShrink: 0,
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                background: step.done
                  ? "var(--tone-success-bg)"
                  : step.current
                    ? "var(--surface-brand-strong)"
                    : "var(--surface-inset)",
                color: step.done
                  ? "var(--tone-success-fg)"
                  : step.current
                    ? "#fff"
                    : "var(--text-muted)",
              }}
            >
              {step.done ? <icons.IconCheck size={16} /> : index + 1}
            </span>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: step.done ? "var(--text-muted)" : "var(--text-strong)",
                }}
              >
                {step.title}
                <span style={VISUALLY_HIDDEN}>{step.done ? " — erledigt" : step.current ? " — als Nächstes" : ""}</span>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)", marginTop: 2 }}>
                {step.text}
              </div>
            </div>
            {step.current && step.action !== null && rights.canWrite && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate({ kind: "site", siteId, area: step.action!.area })}
              >
                {step.action.label}
              </Button>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
